(function () {
  'use strict';

  function parseHex(hex) {
    const h = hex.replace('#', '').trim().toLowerCase();
    if (h.length === 3) {
      return [
        parseInt(h[0] + h[0], 16),
        parseInt(h[1] + h[1], 16),
        parseInt(h[2] + h[2], 16),
      ];
    }
    if (h.length === 6) {
      return [
        parseInt(h.slice(0, 2), 16),
        parseInt(h.slice(2, 4), 16),
        parseInt(h.slice(4, 6), 16),
      ];
    }
    return [17, 17, 17];
  }

  function hexFromRgb(r, g, b) {
    const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));
    return '#' + [clamp(r), clamp(g), clamp(b)].map((x) => x.toString(16).padStart(2, '0')).join('');
  }

  function mixHex(a, b, amount) {
    const amt = Math.max(0, Math.min(1, amount));
    const [ar, ag, ab] = parseHex(a);
    const [br, bg, bb] = parseHex(b);
    const r = ar * (1 - amt) + br * amt;
    const g = ag * (1 - amt) + bg * amt;
    const bl = ab * (1 - amt) + bb * amt;
    return hexFromRgb(r, g, bl);
  }

  function textColorFor(bgHex) {
    const [r, g, b] = parseHex(bgHex);
    const y = (r * 299 + g * 587 + b * 114) / 1000;
    return y >= 128 ? '#111111' : '#ffffff';
  }

  // Colour mapping (longest key first). Covers all 47 colour values from the store.
  const SWATCH_MAP = [
    ['heather midnight navy', '#2b3a55'],
    ['heather true royal', '#4a5aa5'],
    ['heather grass green', '#6fa56f'],
    ['heather forest', '#597a59'],
    ['heather orange', '#f2a66b'],
    ['heather dust', '#e2bfb9'],
    ['athletic heather', '#d8d8d8'],
    ['charcoal heather', '#666666'],
    ['dark grey heather', '#7f7f7f'],
    ['black heather', '#3f3f3f'],
    ['dark grey', '#54585a'],
    ['carbon grey', '#4a4a4a'],
    ['navy blazer', '#1f2a44'],
    ['true royal', '#2a4b9b'],
    ['team royal', '#1f4fd8'],
    ['navy', '#1b2a4a'],
    ['steel blue', '#4682b4'],
    ['sky blue', '#87ceeb'],
    ['light blue', '#a8c8e8'],
    ['ash', '#d6d6d6'],
    ['asphalt', '#3f4448'],
    ['army', '#4b5320'],
    ['olive', '#708238'],
    ['kelly', '#4cbb17'],
    ['mustard', '#e1ad01'],
    ['mauve', '#b784a7'],
    ['maroon', '#800000'],
    ['cranberry', '#a3164a'],
    ['dusty rose', '#d4a5a5'],
    ['lilac', '#c8a2c8'],
    ['pink', '#f5a3c7'],
    ['purple', '#6b3fa0'],
    ['red', '#c8102e'],
    ['team red', '#b31b1b'],
    ['yellow', '#ffd400'],
    ['teal', '#008080'],
    ['khaki', '#c3b091'],
    ['stone', '#cfc5b6'],
    ['bone', '#e8e2d6'],
    ['natural', '#ede4d3'],
    ['soft cream', '#f2e8d5'],
    ['white', '#ffffff'],
    ['black', '#111111'],
    ['vintage black', '#2b2b2b'],
    ['green', '#228b22'],
    ['forest green', '#228b22'],
    ['blue', '#4682b4'],
    ['grey', '#7f7f7f'],
    ['gray', '#7f7f7f'],
  ];

  function resolveColor(name) {
    if (!name) return null;
    const n = name.toLowerCase().replace(/[^a-z\s]/g, ' ').replace(/\s+/g, ' ').trim();
    if (!n) return null;

    let bestKey = null;
    let bestLen = -1;
    for (let i = 0; i < SWATCH_MAP.length; i++) {
      const k = SWATCH_MAP[i][0];
      if (n === k || n.indexOf(k) !== -1) {
        if (k.length > bestLen) {
          bestLen = k.length;
          bestKey = k;
        }
      }
    }
    if (bestKey) {
      const entry = SWATCH_MAP.find((e) => e[0] === bestKey);
      if (!entry) return null;
      let base = entry[1];
      if (n.indexOf('heather') !== -1 && bestKey.indexOf('heather') === -1) {
        base = mixHex(base, '#cfcfcf', 0.45);
      }
      return base;
    }

    // Generic heather blending if the word 'heather' appears but no specific match
    if (n.indexOf('heather') !== -1) {
      return mixHex('#cfcfcf', '#cfcfcf', 0.1);
    }

    return null;
  }

  function applySwatches(root) {
    if (!root) root = document;

    const fieldsets = root.querySelectorAll('fieldset.product-form__input');
    fieldsets.forEach((fs) => {
      const legend = fs.querySelector('legend.form__label');
      if (!legend) return;
      const leg = (legend.textContent || '').trim().toLowerCase();
      if (leg.indexOf('colou') === -1 && leg.indexOf('color') === -1) return;

      const inputs = fs.querySelectorAll('input[type="radio"]');
      inputs.forEach((input) => {
        let label = fs.querySelector('label[for="' + input.id + '"]');
        if (!label) {
          // try next sibling
          let sib = input.nextElementSibling;
          while (sib && sib.tagName !== 'LABEL') sib = sib.nextElementSibling;
          if (sib) label = sib;
        }
        if (!label) return;

        // extract colour name
        const clone = label.cloneNode(true);
        const hidden = clone.querySelector('.visually-hidden');
        if (hidden) hidden.remove();
        const name = (clone.textContent || '').trim();
        const hex = resolveColor(name);

        if (hex) {
          label.setAttribute('data-vl-swatch', 'true');
          label.style.setProperty('--vl-swatch-color', hex);
          label.style.setProperty('--vl-swatch-text', textColorFor(hex));
        } else {
          label.removeAttribute('data-vl-swatch');
          label.style.removeProperty('--vl-swatch-color');
          label.style.removeProperty('--vl-swatch-text');
        }
      });
    });
  }

  function start() {
    applySwatches(document);

    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.addedNodes && m.addedNodes.length > 0) {
          applySwatches(document.body);
          return;
        }
        if (m.type === 'attributes') {
          applySwatches(document.body);
          return;
        }
      }
    });
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
      attributeFilter: ['class', 'style'],
    });

    // handle variant change events (Dawn)
    document.addEventListener('variant:changed', function () {
      setTimeout(function () { applySwatches(document.body); }, 10);
    });
    document.addEventListener('product:variant:change', function () {
      setTimeout(function () { applySwatches(document.body); }, 10);
    });
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }
  }
  try { if (typeof module !== 'undefined') { module.exports = { resolveColor: resolveColor, applySwatches: applySwatches }; } } catch (e) {}
})();
